import React from "react";
import type { Era } from "@/types";

const INK = "#111827";
const PAPER = "#ffffff";

const SUN = { x: 190, y: 62, r: 38 };
const SUN_RAYS = Array.from({ length: 16 }, (_, i) => {
  const a = (i * Math.PI) / 8;
  const outer = i % 2 ? 56 : 66;
  return {
    x1: SUN.x + Math.cos(a) * 46,
    y1: SUN.y + Math.sin(a) * 46,
    x2: SUN.x + Math.cos(a) * outer,
    y2: SUN.y + Math.sin(a) * outer,
  };
});

// Rounded, healthy treeline on the left; bare spiky pines on the right.
const LEFT_TREES =
  "M0 184 " +
  Array.from({ length: 14 }, (_, i) => {
    const x = i * 30;
    const h = [18, 24, 16, 22, 26, 17, 21, 25, 15, 23, 19, 24, 18, 20][i];
    return `Q${x + 15} ${184 - h * 2} ${x + 30} 184`;
  }).join(" ");
const RIGHT_TREES =
  "M400 184 " +
  Array.from({ length: 19 }, (_, i) => {
    const x = 400 + i * 22;
    const h = [
      26, 34, 22, 38, 28, 20, 36, 30, 24, 40, 26, 32, 22, 36, 28, 34, 24, 30,
      26,
    ][i];
    return `L${x + 11} ${184 - h} L${x + 22} 184`;
  }).join(" ");

// One Y-shaped road, symmetric about x = 400.
const ROAD =
  "M330 400 C350 350 365 315 372 292 C300 270 110 262 110 228 " +
  "C110 205 170 196 196 186 L214 186 C196 198 150 208 152 226 " +
  "C154 248 330 256 400 274 C470 256 646 248 648 226 " +
  "C650 208 604 198 586 186 L604 186 C630 196 690 205 690 228 " +
  "C690 262 500 270 428 292 C435 315 450 350 470 400 Z";

interface LandscapeProps {
  id: string;
  ink: string;
  paper: string;
  kidShift: [number, number];
}

/**
 * The whole scene, drawn in a single ink/paper pair. It is rendered twice —
 * black-on-white clipped to the left half, white-on-black clipped to the right —
 * so the pre/post split is a literal inversion of the site's monochrome theme.
 */
const Landscape: React.FC<LandscapeProps> = ({ id, ink, paper, kidShift }) => (
  <g
    stroke={ink}
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    fill="none"
  >
    <defs>
      <pattern
        id={`grass-${id}`}
        width="72"
        height="44"
        patternUnits="userSpaceOnUse"
      >
        <path
          d="M10 18 l-2 -5 M12 18 v-6 M14 18 l2 -5 M46 40 l-2 -4 M48 40 v-5 M50 40 l2 -4"
          strokeWidth={1}
          strokeOpacity={0.7}
        />
      </pattern>
    </defs>

    <rect y="-40" width="800" height="440" fill={paper} stroke="none" />

    {/* Left sky: turning sun, drifting cloud, flapping birds */}
    <g>
      <g className="fork-sun-rays">
        {SUN_RAYS.map((r, i) => (
          <line key={i} {...r} />
        ))}
      </g>
      <circle cx={SUN.x} cy={SUN.y} r={SUN.r} fill={paper} />
      <path
        className="fork-cloud"
        d="M40 -6 c-8 0 -8 -10 0 -10 c2 -8 14 -10 18 -3 c5 -7 18 -5 18 4 c7 0 8 9 0 9 Z"
        fill={paper}
      />
      <g className="fork-birds">
        <path className="fork-bird" d="M52 58 q6 -6 12 0 q6 -6 12 0" />
        <path
          className="fork-bird"
          style={{ animationDelay: "-0.3s" }}
          d="M300 34 q4 -4 8 0 q4 -4 8 0"
        />
      </g>
    </g>

    {/* Right sky: storm clouds and lightning */}
    <g>
      <path
        d="M400 60 C420 32 450 32 465 46 C480 20 520 18 535 40 C555 14 600 14 612 38 C635 18 680 20 690 42 C710 22 760 24 770 44 C782 36 800 40 800 40 L800 -42 L400 -42 Z"
        fill={paper}
      />
      <path
        d="M455 82 C465 64 492 62 502 74 C515 58 548 58 556 74 C570 62 596 66 598 82 Z"
        fill={paper}
      />
      <path
        d="M430 20 l10 10 M470 28 l8 8 M560 26 l10 10 M650 30 l9 9 M740 30 l8 8 M520 70 l6 6"
        strokeWidth={1.2}
      />
      <path
        className="fork-bolt"
        d="M716 40 L700 74 L712 74 L694 116 L728 64 L714 64 L728 40 Z"
        fill={ink}
        stroke="none"
      />
      <path d="M660 46 l5 -4 l3 3 l3 -3 l5 4 M506 104 l4 -3 l2 2 l2 -2 l4 3" />
    </g>

    {/* Treelines */}
    <path d={`${LEFT_TREES} L400 190 L0 190 Z`} fill={paper} />
    <path d={`${RIGHT_TREES} L800 190 L400 190 Z`} fill={paper} />

    {/* Left plateau and castle */}
    <path
      d="M92 184 L104 152 L100 132 L280 132 L276 152 L290 184 Z"
      fill={paper}
    />
    <path d="M112 152 H150 M204 162 H262 M122 172 H168" strokeWidth={1.2} />
    <path d="M205 183 L178 164 L206 146 L192 133" strokeDasharray="3 4" />
    <g fill={paper}>
      <rect x="158" y="94" width="64" height="38" />
      <path d="M158 94 V88 H166 V94 H174 V88 H180 M200 88 H206 V94 H214 V88 H222 V94" />
      <rect x="142" y="72" width="18" height="60" />
      <path d="M139 72 L151 44 L163 72 Z" />
      <path d="M151 44 V34" />
      <path className="fork-flag" d="M151 34 L160 37 L151 40" />
      <rect x="220" y="72" width="18" height="60" />
      <path d="M217 72 L229 44 L241 72 Z" />
      <path d="M229 44 V34" />
      <path
        className="fork-flag"
        style={{ animationDelay: "-0.5s" }}
        d="M229 34 L238 37 L229 40"
      />
      <rect x="180" y="54" width="20" height="40" />
      <path d="M176 54 L190 20 L204 54 Z" />
      <path d="M190 20 V8" />
      <path
        className="fork-flag"
        style={{ animationDelay: "-0.9s" }}
        d="M190 8 L201 11 L190 14"
      />
    </g>
    <g fill={ink} stroke="none">
      <rect x="186" y="64" width="8" height="10" rx="4" />
      <rect x="147" y="90" width="8" height="10" rx="4" />
      <rect x="225" y="90" width="8" height="10" rx="4" />
      <path d="M182 132 V118 Q190 108 198 118 V132 Z" />
    </g>

    {/* Right plateau and castle */}
    <path
      d="M510 184 L524 158 L516 140 L530 132 L700 132 L712 146 L704 162 L716 184 Z"
      fill={paper}
    />
    <path d="M540 148 l10 12 l-4 10 M652 144 l-6 14 l8 12" strokeWidth={1.2} />
    <path d="M598 183 L624 164 L596 148 L612 133" strokeDasharray="3 4" />
    <g fill={paper}>
      <path d="M584 132 L586 98 L642 94 L646 132 Z" />
      <path d="M586 98 V91 H594 V97 H604 V89 H612 V96 L626 95 V90 H633 V95" />
      <g transform="rotate(-6 575 132)">
        <rect x="566" y="70" width="18" height="62" />
        <path d="M562 70 L580 36 L588 70 Z" />
      </g>
      <g transform="rotate(5 651 132)">
        <rect x="642" y="80" width="18" height="52" />
        <path d="M642 80 L646 73 L650 78 L655 70 L660 80" />
      </g>
      <rect x="604" y="48" width="20" height="48" />
      <path d="M600 48 L613 14 L628 48 Z" />
      <path d="M613 14 Q617 8 623 7" />
    </g>
    <g fill={ink} stroke="none">
      <rect x="610" y="60" width="7" height="9" />
      <rect
        x="570"
        y="90"
        width="7"
        height="9"
        transform="rotate(-6 575 132)"
      />
    </g>
    <path d="M648 96 h7 v9 h-7 Z" strokeWidth={1.4} />
    <path d="M606 132 V118 L614 110 L622 118 V132 M610 132 V116 M614 132 V112 M618 132 V116" />
    <path d="M690 132 V108 M690 118 L680 108 L676 102 M690 112 L698 100 L703 102" />

    {/* Ground */}
    <rect x="0" y="184" width="800" height="216" fill={paper} stroke="none" />
    <rect
      x="0"
      y="184"
      width="800"
      height="216"
      fill={`url(#grass-${id})`}
      stroke="none"
    />
    <path d="M0 184 H800" />
    <g fill={paper}>
      <path d="M52 312 q10 -16 26 -8 q9 6 2 12 Z" />
      <path d="M118 244 q6 -9 15 -4 q5 4 1 7 Z" />
      <path d="M748 312 q-10 -16 -26 -8 q-9 6 -2 12 Z" />
      <path d="M682 244 q-6 -9 -15 -4 q-5 4 -1 7 Z" />
    </g>

    {/* Road */}
    <path d={ROAD} fill={paper} />
    <path
      d="M388 372 l5 -1 M416 336 l-4 -1 M180 226 l5 1 M620 226 l-5 1"
      strokeWidth={1.2}
    />

    {/* Signpost */}
    <g fill={paper}>
      <rect x="396" y="194" width="8" height="80" />
      <path d="M398 200 L336 200 L324 211 L336 222 L398 222 Z" />
      <path d="M402 228 L464 228 L476 239 L464 250 L402 250 Z" />
    </g>
    <g
      fill={ink}
      stroke="none"
      fontFamily='ui-monospace, SFMono-Regular, "SF Mono", Menlo, monospace'
      fontSize="10"
      fontWeight="700"
      letterSpacing="0.5"
      textAnchor="middle"
    >
      <text x="364" y="215">
        PRE-GPT
      </text>
      <text x="436" y="243">
        POST-GPT
      </text>
    </g>

    {/* The one choosing */}
    <g
      className="fork-kid"
      style={{ transform: `translate(${kidShift[0]}px, ${kidShift[1]}px)` }}
    >
      <path d="M396 350 V367 M404 350 V367" />
      <ellipse cx="395" cy="369" rx="4" ry="2" fill={ink} />
      <ellipse cx="405" cy="369" rx="4" ry="2" fill={ink} />
      <rect x="391" y="340" width="18" height="11" fill={ink} />
      <path d="M389 342 L391 318 Q400 312 409 318 L411 342 Z" fill={paper} />
      <path d="M391 321 L386 340 M409 321 L414 340" />
      <circle cx="400" cy="304" r="11" fill={paper} />
      <path
        d="M389 303 Q391 290 400 292 Q409 290 411 303 L406 298 L402 302 L398 297 L394 302 Z"
        fill={ink}
      />
    </g>
  </g>
);

interface ForkInTheRoadProps {
  road: Era | null;
  hovered: Era | null;
  onHover: (era: Era | null) => void;
  onChoose: (era: Era) => void;
}

const KID_SHIFT: Record<"none" | Era, [number, number]> = {
  none: [0, 0],
  pre: [-46, -34],
  post: [46, -34],
};

/**
 * Decorative, pointer-only illustration. Keyboard and screen-reader users get
 * the equivalent buttons rendered by the page right below it.
 */
const ForkInTheRoad: React.FC<ForkInTheRoadProps> = ({
  road,
  hovered,
  onHover,
  onChoose,
}) => {
  const kidShift = KID_SHIFT[road ?? "none"];
  // The chosen road stays lit; the other side fades. Hover only previews it.
  const focus = road ?? hovered;
  const awayOpacity = road ? 0.72 : 0.4;
  const preDim = focus === "post" ? awayOpacity : 0;
  const postDim = focus === "pre" ? awayOpacity : 0;

  return (
    <svg
      viewBox="0 -40 800 440"
      className="block w-full h-auto select-none"
      aria-hidden="true"
      onMouseLeave={() => onHover(null)}
    >
      <defs>
        <clipPath id="fork-left">
          <rect x="0" y="-40" width="400" height="440" />
        </clipPath>
        <clipPath id="fork-right">
          <rect x="400" y="-40" width="400" height="440" />
        </clipPath>
      </defs>

      <g clipPath="url(#fork-left)">
        <Landscape id="l" ink={INK} paper={PAPER} kidShift={kidShift} />
      </g>
      <g clipPath="url(#fork-right)">
        <Landscape id="r" ink={PAPER} paper={INK} kidShift={kidShift} />
      </g>

      <rect
        className="fork-dim"
        x="0"
        width="400"
        y="-40"
        height="440"
        fill={PAPER}
        opacity={preDim}
      />
      <rect
        className="fork-dim"
        x="400"
        width="400"
        y="-40"
        height="440"
        fill={INK}
        opacity={postDim}
      />

      <rect
        x="0"
        width="400"
        y="-40"
        height="440"
        fill="transparent"
        className="cursor-pointer"
        onMouseEnter={() => onHover("pre")}
        onClick={() => onChoose("pre")}
      />
      <rect
        x="400"
        width="400"
        y="-40"
        height="440"
        fill="transparent"
        className="cursor-pointer"
        onMouseEnter={() => onHover("post")}
        onClick={() => onChoose("post")}
      />
    </svg>
  );
};

export default ForkInTheRoad;
